/**
 * MapProvider — ONE shared mapboxgl.Map per session (Wave 2 / #1, OPEN-006).
 *
 * Both map surfaces (Operations Map View + the Geofence editor) attach the SAME
 * map instance via container-teleport instead of each creating their own. The map
 * lives in a dedicated, provider-owned `<div>` (created with document.createElement,
 * NOT rendered by React) so the teleported node is never a consumer's React-managed
 * surface div — that avoids React-ownership conflicts and the self-append
 * (`el.appendChild(el)`) HierarchyRequestError that occurred when a surface
 * re-acquired the container it already held (StrictMode remount / route round-trip).
 *   - acquire(target): appends the provider-owned map div into `target` (a real
 *     sized container — avoids 0×0 WebGL-init issues), lazily creating the map into
 *     that div on first call and resizing on later calls. Returns the map (or null
 *     when no token / live-map disabled, so callers render a fallback).
 *   - release(): moves the map div back to a hidden holder so it survives
 *     route/tab switches WITHOUT being destroyed (one map load per session).
 *
 * IMPORTANT for callers: the map is NEVER `.remove()`d between surfaces, so each
 * surface MUST tear down ITS OWN listeners (map.off), markers, and sources/layers
 * on unmount — otherwise they leak onto the next surface. The provider only owns
 * the map's lifecycle (create once, destroy on provider unmount / logout).
 */

import * as React from 'react';
import mapboxgl from 'mapbox-gl';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';

const DEFAULT_CENTER: [number, number] = [-97.5, 35.5];
const DEFAULT_ZOOM = 7;
const DEFAULT_STYLE = 'mapbox://styles/mapbox/dark-v11';

export interface MapContextValue {
  /** Move/create the shared map into `target`; null if no Mapbox token. */
  readonly acquire: (target: HTMLElement) => mapboxgl.Map | null;
  /** Park the shared map in the hidden holder (keeps it alive across surfaces). */
  readonly release: () => void;
  /** Whether a Mapbox token is configured (live map possible). */
  readonly hasToken: boolean;
}

const MapContext = React.createContext<MapContextValue | null>(null);

const MAPBOX_TOKEN = import.meta.env['VITE_MAPBOX_TOKEN'] as string | undefined;

export function MapProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const mapRef = React.useRef<mapboxgl.Map | null>(null);
  const holderRef = React.useRef<HTMLDivElement | null>(null);
  // Dedicated, provider-owned map container — created once, NEVER rendered by
  // React. The map is created into this node and this node is what teleports
  // between surfaces, so we never appendChild a consumer's React div into itself.
  const mapDivRef = React.useRef<HTMLDivElement | null>(null);

  const apiOpts = useApiOpts();

  /**
   * 86baefu3r — report one Mapbox map load for the signed-in user's tenant.
   *
   * FIRE AND FORGET, and it must stay that way: a cost counter is never worth a broken
   * dispatch map. Network failure is swallowed, a non-2xx is ignored (authedFetch only
   * rejects on transport errors), and nothing here is awaited, so the map is returned to
   * the caller on the same tick regardless.
   *
   * The body is an explicit `{}`. A POST with a JSON content-type and NO body makes
   * Fastify 400 — the DEC-201 trap — and authedFetch does not supply the default that
   * useApiMutation does.
   *
   * No "already reported" ref on purpose. The caller's `if (!mapRef.current)` is the same
   * condition that gates construction, so this already fires exactly once per real map.
   * A second guard here would, if a map were ever destroyed and rebuilt inside one
   * provider lifetime, silently swallow a genuinely billable second load — and for a cost
   * figure, overcounting a real event is the better failure than undercounting one.
   */
  const reportMapLoad = React.useCallback((): void => {
    void authedFetch(apiOpts, '/v1/map-usage/load', {
      method: 'POST',
      body: JSON.stringify({}),
    }).catch(() => {
      // Deliberately silent. Nothing the dispatcher can do about it, and surfacing it
      // would turn a billing-telemetry hiccup into a user-visible map error.
    });
  }, [apiOpts]);

  const getMapDiv = React.useCallback((): HTMLDivElement => {
    if (!mapDivRef.current) {
      const div = document.createElement('div');
      // Fill the host via ABSOLUTE positioning, NOT height:100%. The teleported
      // mapDiv is a nested child, and a nested `height:100%` does not reliably
      // resolve through a flex parent chain — on the Geofences surface it
      // collapsed mapDiv to 0px tall, so Mapbox's canvas measured 0 and the map
      // rendered blank (the dedicated-map build worked only because Mapbox read
      // the container's real clientHeight directly). Absolute inset:0 sizes mapDiv
      // against the host's rendered pixel box with no percentage resolution — the
      // host just has to be a positioned ancestor (ensured in acquire()).
      div.style.position = 'absolute';
      div.style.top = '0';
      div.style.left = '0';
      div.style.right = '0';
      div.style.bottom = '0';
      mapDivRef.current = div;
    }
    return mapDivRef.current;
  }, []);

  const acquire = React.useCallback(
    (target: HTMLElement): mapboxgl.Map | null => {
      if (!MAPBOX_TOKEN) return null;
      const mapDiv = getMapDiv();
      // The mapDiv fills its host by absolute positioning, so the host must be a
      // positioned ancestor. Promote a `static` host to `relative` (the host is an
      // empty leaf container that only hosts the map, so this has no side effects
      // on the surface's own absolutely-positioned overlays, which anchor to a
      // different ancestor).
      if (window.getComputedStyle(target).position === 'static') {
        target.style.position = 'relative';
      }
      // Move the dedicated map div into the active surface (idempotent — skip if
      // already there). target is always a different node than mapDiv, so this is
      // never a self-append.
      if (mapDiv.parentElement !== target) {
        target.appendChild(mapDiv);
      }
      if (!mapRef.current) {
        mapboxgl.accessToken = MAPBOX_TOKEN;
        mapRef.current = new mapboxgl.Map({
          container: mapDiv,
          style: DEFAULT_STYLE,
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
        });
        // 86baefu3r — report the map load for per-tenant cost tracking.
        //
        // INSIDE this `if`, not in acquire()'s body: Mapbox bills per map
        // INSTANTIATION, and this block is the only place a map is constructed. acquire()
        // runs on every surface mount and route round-trip, so counting there would
        // multiply the real figure by navigation and make the cost console wrong in a
        // direction nobody would notice.
        reportMapLoad();
      }
      // Recompute the canvas size after the (newly-sized) host lays out. Layout is
      // not necessarily final on the same tick as the DOM move, so resize across a
      // macrotask AND two animation frames — cheap, and covers the teleport race.
      const doResize = (): void => {
        mapRef.current?.resize();
      };
      window.setTimeout(doResize, 0);
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
          doResize();
          window.requestAnimationFrame(doResize);
        });
      }
      return mapRef.current;
    },
    [getMapDiv, reportMapLoad],
  );

  const release = React.useCallback((): void => {
    if (mapDivRef.current && holderRef.current) {
      holderRef.current.appendChild(mapDivRef.current);
    }
  }, []);

  // Destroy the single map only when the provider unmounts (e.g. sign-out).
  React.useEffect(() => {
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
      mapDivRef.current = null;
    };
  }, []);

  const value = React.useMemo<MapContextValue>(
    () => ({ acquire, release, hasToken: Boolean(MAPBOX_TOKEN) }),
    [acquire, release],
  );

  return (
    <MapContext.Provider value={value}>
      {children}
      {/* Hidden holder — parks the live map between surfaces so it is never re-created. */}
      <div
        ref={holderRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
          visibility: 'hidden',
        }}
      />
    </MapContext.Provider>
  );
}

export function useSharedMap(): MapContextValue {
  const ctx = React.useContext(MapContext);
  if (ctx === null) {
    throw new Error('useSharedMap() called outside <MapProvider>.');
  }
  return ctx;
}
